import { Box, Button, Typography } from "@mui/material";
import { useQuery } from "react-query";
import StatsCard from "src/components/common/StatsCard";

import defaultStyles, { StylesClasses } from "../styles";
import { getStyles } from "src/styles/theme";
import {
  getLandDashboardDetails,
  getTableDashboardDetails,
} from "src/utils/api/dashboard";
import {
  TableformatToIndianCurrency,
  downloadSheet,
  exportTableToXlsx,
  formatData,
  formatDataForBarChart,
  formatToIndianCurrency,
  fundApplicationExportTableToXlsx,
} from "src/utils/helper";
import DashboardChartSkeleton from "src/components/common/DashboardChartSkeleton";
import { useEffect, useMemo, useState } from "react";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";
import StatusBarChart from "src/components/common/StatusBarChart";
import RenderTable from "../../Home/RenderTable";
import { costEstimateDashboardtableColumns } from "src/constants/tableColumns";
import { AxiosResponse } from "axios";
import {
  DashBoardTable,
  FundApplicationDashBoardTable,
} from "src/components/common/DashBoardTable/fundApplicationDashboard";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";

const FundApplicationDashboard = () => {
  const [isTabLoading, setIsTabLoading] = useState(true);

  const [states, setStates] = useState<any>([]);

  const [tableRows, setTableRows] = useState<any[]>([]);

  const [fundApplicationYears, setFundApplicationYears] = useState<number[]>(
    []
  );

  const [isDivisionWise, setIsDivisionWise] = useState(false);

  const [filtersState, setFiltersState] = useState([]);

  const { persisted, saveFilters } = usePersistedFilters("mw_fundApplication");

  const [queryParams, setQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "registrationAndApplicationStatus",
  });

  const [boxQueryParams, setBoxQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "registrationAndApplicationStatusBoxes",
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null }
  );

  const updateStateFilter = (stateIds: string[] | null) => {
    const selectedStateIds = stateIds?.length ? stateIds : null;

    setFilterKey((previousFilter) => ({
      ...previousFilter,
      projectStateIds: selectedStateIds,
    }));
    setQueryParams((previousParams) => ({
      ...previousParams,
      projectStateIds: selectedStateIds ?? undefined,
    }));
    setBoxQueryParams((previousParams) => ({
      ...previousParams,
      projectStateIds: selectedStateIds ?? undefined,
    }));
  };

  const handleStateClick = (stateName: string) => {
    const clickedState = states.find(
      (state) =>
        String(state.name).trim().toLocaleLowerCase() ===
        String(stateName).trim().toLocaleLowerCase()
    );
    if (!clickedState?.id) return;

    const selectedStateIds: string[] = filterKey.projectStateIds ?? [];
    const nextStateIds = selectedStateIds.includes(clickedState.id)
      ? selectedStateIds.filter((stateId) => stateId !== clickedState.id)
      : [...selectedStateIds, clickedState.id];

    updateStateFilter(nextStateIds);
  };

  const { data: dashboard, isLoading: dashboardLoading } = useQuery(
    ["fundApplicationDashboard", boxQueryParams],
    () => getTableDashboardDetails(boxQueryParams),
    {
      onSuccess: () => {},
      onError: () => {},
      refetchOnMount: true,
    }
  );

  const { data: tableDashboard, isLoading } = useQuery(
    ["fundApplication", queryParams],
    () => getTableDashboardDetails(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        const faYears: number[] =
          res?.data?.fundApplicationYears ??
          Object.keys(res?.data?.stats?.[0]?.faPaymentReleased ?? {}).map(
            (key) => Number(key.replace("FA", ""))
          );

        setFundApplicationYears(faYears);
        setIsDivisionWise(Boolean(res?.data?.groupedByDivision));
        setTableRows(
          res?.data?.stats.map((items, idx) => ({
            id: idx,
            sno: idx + 1,
            state: items.state,
            division: items.division ?? "",
            // totalAreaSelected: items.totalAreaSelected,
            // totalNumberSelected: items.totalNumberSelected,
            // totalNumberApproved: items.totalNumberApproved,
            // totalAreaApproved: items.totalAreaApproved,
            totalAreaDemandNoteRaised: items.totalAreaDemandNoteRaised,
            totalNumberDemandNoteRaised: items.totalNumberDemandNoteRaised,
            totalAmountDemandNoteRaised: TableformatToIndianCurrency(
              items.totalAmountDemandNoteRaised
            ),
            totalAreaPaymentReceived: items.totalAreaPaymentReceived,
            totalNumberPaymentReceived: items.totalNumberPaymentReceived,
            totalAmountPaymentReceived: TableformatToIndianCurrency(
              items.totalAmountPaymentReceived
            ),
            totalEcoRestorationCost: TableformatToIndianCurrency(
              items.totalEcoRestorationCost ?? 0
            ),
            // totalAreaFundApplicationPending:
            //   items.totalAreaFundApplicationPending,
            // totalNumberFundApplicationPending:
            //   items.totalNumberFundApplicationPending,
            totalAmountFundApplicationPending: TableformatToIndianCurrency(
              items.totalAmountFundApplicationPending
            ),
            // totalAreaFundApplicationReceived:
            //   items.totalAreaFundApplicationReceived,
            // totalNumberFundApplicationReceived:
            //   items.totalNumberFundApplicationReceived,
            // totalAmountFundApplicationReceived: TableformatToIndianCurrency(
            //   items.totalAmountFundApplicationReceived,
            // ),
            totalAreaPaymentReleased:
              items.totalAreaPaymentReleased ??
              items.totalAreaFirstYearPaymentReleased,
            totalNumberPaymentReleased:
              items.totalNumberPaymentReleased ??
              items.totalNumberFirstYearPaymentReleased,
            totalAmountPaymentReleased: TableformatToIndianCurrency(
              items.totalAmountPaymentReleased ??
                items.totalAmountFirstYearPaymentReleased
            ),
            totalFinancialProgress: TableformatToIndianCurrency(
              items.totalFinancialProgress ?? 0
            ),
            faPaymentReleased: Object.fromEntries(
              faYears.map((year) => {
                const release = items.faPaymentReleased?.[`FA${year}`];
                return [
                  `FA${year}`,
                  {
                    totalNumber: release?.totalNumber ?? 0,
                    totalAmount: TableformatToIndianCurrency(
                      release?.totalAmount ?? 0
                    ),
                    totalUcUploaded: release?.totalUcUploaded ?? 0,
                    totalUcPending: release?.totalUcPending ?? 0,
                  },
                ];
              })
            ),
          }))
        );
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const formattedData = useMemo(
    () => formatDataForBarChart(dashboard?.data?.stats),
    [dashboard?.data?.stats]
  );

  const dashboardTableData: DashBoardTable = {
    customTopheader: [
      { title: "", size: isDivisionWise ? 3 : 2 },
      // { title: "Approved Eco-Restoration Blocks", size: 2 },
      // { title: "Selected Eco-Restoration Blocks", size: 2 },
      { title: "Demand Note Raised", size: 2 },
      { title: "Payment Received", size: 1 },
      { title: "Eco-Restoration Cost", size: 1 },
      // { title: "Fund Application Pending for 2024-25", size: 2 },
      // { title: "Fund Application Received for 2024-25", size: 3 },
      { title: "Total Payment Released", size: 2 },
      { title: "Total Financial Progress", size: 1 },
      ...fundApplicationYears.map((year) => ({
        title: `FA${year}`,
        size: 4,
      })),
      { title: "Balance Amount with Admin", size: 1 },
    ],
    header: [
      "S.No",
      "State",
      ...(isDivisionWise ? ["Division"] : []),
      "No",
      "Amount (₹)",
      "Amount (₹)",
      "Amount (₹)",
      "No",
      "Amount (₹)",
      "Amount (₹)",
      ...fundApplicationYears.flatMap(() => [
        "No",
        "Amount (₹)",
        "UC Accept",
        "UC Under Verification",
      ]),
      "Amount (₹)",
    ],
    data: tableRows,
  };
  const styles = getStyles(defaultStyles);

  useEffect(() => {
    setFiltersState(dashboard?.data?.state);
  }, [dashboard]);

  useEffect(() => {
    saveFilters({ queryParams, filterKey });
  }, [queryParams, filterKey]);

  useEffect(() => {
    setIsTabLoading(true);
  }, []);

  return (
    <>
      <Box {...styles("topHeader")}>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <MultiSelect
            options={states}
            value={filterKey.projectStateIds}
            onChange={(stateIds: string[]) => updateStateFilter(stateIds)}
          />
        </Box>
        {!dashboardLoading ? (
          <Box
            sx={{
              width: "100%",
              marginBottom: "20px",
              marginTop: "5px",
              alignSelf: "center",
            }}
          >
            <StatusBarChart
              data={formattedData}
              label={
                "Status of Payment and Fund Application of Eco-Restoration Blocks"
              }
            />
          </Box>
        ) : (
          <></>
        )}
        <Box {...styles("statsCardWrapper", { marginTop: "20px" })}>
          {dashboard?.data?.stats?.map((stat, index) => (
            <StatsCard
              key={index}
              title={`${stat?.description?.toUpperCase()}`}
              value={`${stat?.number}`}
              area={stat?.totalArea}
              customStyles={{ valueCard: styles("statsCard").sx }}
              link={""}
            />
          ))}
        </Box>
      </Box>
      {isLoading || isTabLoading ? (
        <DashboardChartSkeleton />
      ) : (
        <Box {...styles("dashboardTableWrapper")}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h5" {...styles("heading")}>
              Status of Payment and Fund Application
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                fundApplicationExportTableToXlsx(
                  dashboardTableData.data,
                  dashboardTableData.header,
                  dashboardTableData.customTopheader,
                  fundApplicationYears
                )
              }
            >
              Export
            </Button>
          </Box>
          <FundApplicationDashBoardTable
            {...dashboardTableData}
            fundApplicationYears={fundApplicationYears}
            onStateClick={handleStateClick}
          />
        </Box>
      )}
    </>
  );
};

export default FundApplicationDashboard;
